/**
 * Life4Earth V2 — Curve Metadata API Adapter
 *
 * Replaces: localStorage keys l4e_progress_curve_meta_v2, l4e_finance_cost_meta_v1
 * Endpoint: /api/CurveMeta
 */

import { apiFetch } from "./client";
import { normalizeGuid } from "../utils/guid";

// ── Types ────────────────────────────────────────────────────

export interface CurveMetaResponse {
  id: string;
  projectId: string;
  instanceGuid: string | null;
  category: string; // "schedule" | "finance"
  metaJson: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCurveMetaRequest {
  projectId: string;
  instanceGuid?: string;
  category: string;
  metaJson: Record<string, any>;
}

// ── API Functions ────────────────────────────────────────────

/**
 * Fetch ALL curve meta entries. Optionally filter by category.
 */
export async function fetchAllCurveMeta(
  category?: string
): Promise<CurveMetaResponse[]> {
  const params = new URLSearchParams();
  if (category) params.set("category", category);
  const qs = params.toString();
  return apiFetch<CurveMetaResponse[]>(
    `/api/CurveMeta/all${qs ? `?${qs}` : ""}`
  );
}

/**
 * Fetch curve meta for a specific project.
 */
export async function fetchCurveMetaByProject(
  projectId: string,
  category?: string,
  instanceGuid?: string
): Promise<CurveMetaResponse[]> {
  const params = new URLSearchParams({ projectId });
  if (category) params.set("category", category);
  if (instanceGuid) params.set("instanceGuid", instanceGuid);
  return apiFetch<CurveMetaResponse[]>(
    `/api/CurveMeta?${params.toString()}`
  );
}

/**
 * Create or update a single curve meta entry.
 * Upsert on (project_id, instance_guid, category).
 */
export async function upsertCurveMeta(
  request: CreateCurveMetaRequest
): Promise<CurveMetaResponse> {
  return apiFetch<CurveMetaResponse>("/api/CurveMeta", {
    method: "POST",
    body: JSON.stringify(request),
  });
}

/**
 * Batch upsert: insert or update multiple curve meta entries.
 * Matched on (project_id, instance_guid, category).
 * Max 200 items per batch.
 */
export async function batchUpsertCurveMeta(
  items: CreateCurveMetaRequest[]
): Promise<CurveMetaResponse[]> {
  return apiFetch<CurveMetaResponse[]>("/api/CurveMeta/batch", {
    method: "POST",
    body: JSON.stringify({ items }),
  });
}

/**
 * Convert a CurveMetaResponse into the shape used by override files.
 * Returns { key: "PROJECT_GUID::INSTANCE_GUID", meta: { ...metaJson } }
 */
export function curveMetaToStoreShape(
  r: CurveMetaResponse
): { key: string; category: string; meta: Record<string, any> } {
  const p = normalizeGuid(r.projectId);
  const i = normalizeGuid(r.instanceGuid);
  const key = i ? `${p}::${i}` : p;
  return { key, category: r.category, meta: r.metaJson || {} };
}

export async function deleteCurveMeta(id: string): Promise<void> {
  return apiFetch<void>(`/api/CurveMeta/${id}`, {
    method: "DELETE",
  });
}
